import { useNavigate } from 'react-router-dom';
import { useCallback, useState, useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { type AuthSchema, authSchema } from '@schemas/auth-schema';
import useAuth from '@hooks/use-auth';
import { pageUrl } from '@constants/url';
import { mfaService } from '../../services/mfa-service';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import axiosInstance from '@libs/axios';
import { useLanguage } from '@contexts/LanguageContext';
import type { CubeActiveAccount, CubeSubscriptionResult, CubeSsoLoginPayload } from '@/types/cube-sso';
import {
  CUBE_AUTH_SOURCE,
  CUBE_EXTERNAL_PROVIDER,
  getCubeIdentityBundle,
  initializeCubeSdk,
  triggerCubeLogin,
} from '@/services/cube-sso';
import { clearJarvisSession, completeAuthenticatedSession, hydrateSessionFromMasterUser } from '@utils/auth-session';
import { CircleAlert } from 'lucide-react';
import { AuthFrame } from '@/components/auth-frame/AuthFrame';
import { AuthLanguageSwitch } from '@/components/auth-frame/AuthLanguageSwitch';
import { PasswordInput } from '@/components/auth-frame/PasswordInput';
import { Spinner } from '@/components/auth-frame/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/components/lumen/alert';
import { Button } from '@/components/lumen/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/lumen/form';
import { Input } from '@/components/lumen/input';
import { MFAAddDeviceDialog } from '@/components/mfa-dialogs/MFAAddDeviceDialog';
import { MFASetupDialog } from '@/components/mfa-dialogs/MFASetupDialog';
import { MFAVerifyDialog } from '@/components/mfa-dialogs/MFAVerifyDialog';

/** The server's own words for a failed call, when it sent any. */
const serverMessage = (error: unknown): string | undefined => {
  const data = (error as AxiosError<{ error?: { message?: string }; message?: string }>)?.response?.data;
  return data?.error?.message || data?.message || undefined;
};

/*
 * Sign-in (/auth). The logic below is the old page's, moved intact: the same requests in the same
 * order, the same sessionStorage / localStorage / cookie writes and the same redirect rule. What
 * changed is the UI: Lumen components in AuthFrame, sonner toasts with the same copy, and inline
 * errors next to the toasts (the server's message where it sends one).
 */
export default function LoginPage() {
  const { isLoading, login, loginWithCubeSso, setIsAuthenticated, error: loginError } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [mfaSetupData, setMFASetupData] = useState<{
    qrCodeUrl: string;
    secret: string;
    backupCodes: string[];
    deviceId: number;
  } | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [showMFAVerify, setShowMFAVerify] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [cubeError, setCubeError] = useState('');
  const [isCubeLoading, setIsCubeLoading] = useState(false);
  // UI only: which sign-in the hook's `error` belongs to, and why the last MFA code was refused.
  const [attempt, setAttempt] = useState<'password' | 'cube' | null>(null);
  const [verifyError, setVerifyError] = useState('');
  const [setupError, setSetupError] = useState('');

  useEffect(() => {
    void initializeCubeSdk({
      onError: (error: unknown) => {
        console.error('[CUBE SSO] SDK callback error:', error);
      },
    }).catch((error) => {
      console.warn('[CUBE SSO] SDK initialization skipped:', error);
    });
  }, []);

  // Check on mount if user is logged in but MFA not enabled
  useEffect(() => {
    const accessToken = Cookies.get('accessToken') || localStorage.getItem('accessToken');
    const mfaEnabled = localStorage.getItem('mfaEnabled');
    const authData = localStorage.getItem('authData');

    // Check if user was in the middle of MFA verification (session token exists)
    const storedSessionToken = sessionStorage.getItem('mfaSessionToken');
    const storedEmail = sessionStorage.getItem('mfaEmail');

    if (storedSessionToken && storedEmail) {
      setSessionToken(storedSessionToken);
      setUserEmail(storedEmail);
      setShowMFAVerify(true);
      return;
    }

    // Only redirect if user is fully authenticated (has token AND MFA is enabled)
    if (accessToken && mfaEnabled === 'true') {
      const parsedAuthData = authData ? JSON.parse(authData) : null;
      const email = parsedAuthData?.email || '';
      // Redirect Samuel to dashboard, others to doc-pabean
      if (email.toLowerCase().includes('samuel')) {
        navigate(pageUrl.dashboard);
      } else {
        navigate(pageUrl.docPabean);
      }
      return;
    }

    // If user has token but MFA is not enabled, they need to complete MFA setup
    // BUT don't automatically trigger setup on refresh - let them click login again
    if (accessToken && mfaEnabled === 'false' && authData) {
      const parsedAuthData = JSON.parse(authData);
      const email = parsedAuthData.email;
      setUserEmail(email);

      // Clear the incomplete auth state
      // User needs to login again to properly set up MFA
      localStorage.removeItem('accessToken');
      localStorage.removeItem('authData');
      localStorage.removeItem('mfaEnabled');
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
    }
  }, []);

  const form = useForm({
    resolver: yupResolver(authSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const { handleSubmit, control } = form;

  const redirectAfterLogin = useCallback(
    (email: string) => {
      // Redirect Samuel to dashboard, others to doc-pabean
      if (email.toLowerCase().includes('samuel')) {
        navigate(pageUrl.dashboard);
      } else {
        navigate(pageUrl.docPabean);
      }
    },
    [navigate]
  );

  const handleLogin: SubmitHandler<AuthSchema> = async (data) => {
    setUserEmail(data.email);
    setCubeError('');
    setAttempt('password');

    try {
      // Call login API
      const loginResult = await login(data);

      // Login returned null/undefined = failed inside useAuth
      if (!loginResult) {
        return;
      }

      // Check if MFA verification is required (user has MFA enabled)
      if (loginResult.kind === 'mfa_required') {
        // Store session token in sessionStorage so it persists on refresh
        sessionStorage.setItem('mfaSessionToken', loginResult.sessionToken);
        sessionStorage.setItem('mfaEmail', data.email);
        // Store session token and show MFA verification modal
        setSessionToken(loginResult.sessionToken);
        setShowMFAVerify(true);
        return;
      }

      // If login was successful, check MFA status
      if (loginResult.kind === 'authenticated') {
        // Check if MFA is enabled for this user
        if (loginResult.enabledMfa === false) {
          // User needs to set up MFA - don't redirect yet
          await setupMFA(data.email);
        } else {
          // MFA is enabled and verified, redirect to app
          redirectAfterLogin(data.email);
        }
      }
    } catch (err) {
      console.error('[LOGIN] Unexpected error:', err);
    }
  };

  const buildCubePayload = (): Promise<CubeSsoLoginPayload> => {
    return getCubeIdentityBundle().then(
      ({
        activeAccount,
        subscription,
      }: {
        activeAccount: CubeActiveAccount | null;
        subscription: CubeSubscriptionResult | null;
      }) => {
        const email = activeAccount?.email;
        const fullName = activeAccount?.fullName || activeAccount?.name || activeAccount?.displayName;
        const externalSubject =
          typeof activeAccount?.sub === 'string'
            ? activeAccount.sub
            : typeof activeAccount?.oid === 'string'
              ? activeAccount.oid
              : typeof activeAccount?.userId === 'string'
                ? activeAccount.userId
                : undefined;
        const isActive = subscription?.isActive ?? subscription?.active ?? false;
        const companyName =
          typeof subscription?.companyName === 'string'
            ? subscription.companyName
            : typeof activeAccount?.companyName === 'string'
              ? activeAccount.companyName
              : undefined;
        const companyId =
          typeof subscription?.companyId === 'string'
            ? subscription.companyId
            : typeof activeAccount?.companyId === 'string'
              ? activeAccount.companyId
              : undefined;

        if (!email) {
          throw new Error('CUBE SSO login failed: account email is missing');
        }

        if (!fullName) {
          throw new Error('CUBE SSO login failed: account full name is missing');
        }

        if (!isActive) {
          throw new Error('Access denied: your CUBEforall subscription is inactive');
        }

        return {
          email,
          fullName,
          authSource: CUBE_AUTH_SOURCE,
          externalProvider: CUBE_EXTERNAL_PROVIDER,
          externalSubject,
          subscription: {
            isActive: true,
            status: typeof subscription?.status === 'string' ? subscription.status : 'ACTIVE',
            plan: typeof subscription?.plan === 'string' ? subscription.plan : undefined,
            raw: subscription ?? undefined,
          },
          companyName,
          companyId,
          rawClaims: activeAccount ? { ...activeAccount } : undefined,
        };
      }
    );
  };

  const handleCubeLogin = async () => {
    setCubeError('');
    setIsCubeLoading(true);
    setAttempt('cube');

    try {
      await triggerCubeLogin();
      const payload = await buildCubePayload();
      const result = await loginWithCubeSso(payload);

      if (!result?.success) {
        throw new Error('CUBE SSO exchange failed');
      }

      toast.success(t('lumen.auth.toast.loginSuccessful'), { description: t('lumen.auth.toast.cubeSignedIn') });

      redirectAfterLogin(payload.email);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'CUBE SSO login failed';
      setCubeError(message);
      clearJarvisSession();
      setIsAuthenticated(false);
      toast.error(t('lumen.auth.toast.cubeError'), { description: message });
    } finally {
      setIsCubeLoading(false);
    }
  };

  const setupMFA = async (email: string) => {
    try {
      // Get full email from auth data or parameter
      const authData = localStorage.getItem('authData');
      let userEmail = email;
      if (authData) {
        const parsed = JSON.parse(authData);
        userEmail = parsed.email || email;
      }

      // Call setup API
      const setupResponse = await mfaService.setupMFA('Smartphone', userEmail);

      // Show MFA setup modal
      setMFASetupData({
        qrCodeUrl: setupResponse.data.qrCodeUrl,
        secret: setupResponse.data.secret,
        backupCodes: setupResponse.data.backupCodes,
        deviceId: setupResponse.data.deviceId,
      });
      setSetupError('');
      setShowMFASetup(true);
    } catch (error: unknown) {
      console.error('Error setting up MFA:', error);

      // Check if it's a specific error that indicates MFA is already being set up
      const axiosError = error as AxiosError<{ error?: { message?: string }; message?: string }>;
      const errorMessage = axiosError.response?.data?.error?.message || axiosError.response?.data?.message;
      const isAlreadySetupError =
        errorMessage?.toLowerCase().includes('already') ||
        errorMessage?.toLowerCase().includes('pending') ||
        axiosError.response?.status === 409;

      if (isAlreadySetupError) {
        // Check if we can get the existing setup from the API
        try {
          const statusResponse = await mfaService.getStatus();
          if (statusResponse.data.mfaEnabled) {
            // MFA is actually already enabled, update state and redirect
            localStorage.setItem('mfaEnabled', 'true');
            setIsAuthenticated(true);
            redirectAfterLogin(email);
            return;
          }
        } catch (statusError) {
          console.error('Failed to check MFA status:', statusError);
        }

        // Show a more user-friendly message
        toast.warning(t('lumen.auth.toast.mfaSetup'), { description: t('lumen.auth.toast.mfaSetupPending') });
      } else {
        // Show error for other types of failures
        toast.error(t('lumen.auth.toast.error'), { description: t('lumen.auth.toast.mfaSetupFailed') });
      }

      // On error, clear auth state to allow fresh login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('authData');
      localStorage.removeItem('mfaEnabled');
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
    }
  };

  const _checkMFAStatus = useCallback(
    async (email: string) => {
      try {
        // Verify token exists before calling API
        const cookieToken = Cookies.get('accessToken');
        const localToken = localStorage.getItem('accessToken');

        if (!cookieToken && !localToken) {
          console.error('No token found! Cannot check MFA status');
          redirectAfterLogin(email);
          return;
        }

        // Check MFA status
        try {
          const statusResponse = await mfaService.getStatus();

          if (!statusResponse.data.mfaEnabled) {
            // MFA not enabled, trigger setup
            await setupMFA(email);
          } else {
            // MFA already enabled, redirect to app
            redirectAfterLogin(email);
          }
        } catch (statusError) {
          console.error('Error from MFA status API:', statusError);

          const status = (statusError as AxiosError)?.response?.status;

          // If we get 401 or 404 right after login, it's likely a backend issue
          // Just skip MFA setup and redirect to app
          if (status === 401 || status === 404) {
            // Don't show notification for this - it's expected during development
            redirectAfterLogin(email);
          } else {
            // For other errors, show error and redirect
            toast.error(t('lumen.auth.toast.error'), { description: t('lumen.auth.toast.mfaStatusFailed') });
            redirectAfterLogin(email);
          }
        }
      } catch (error) {
        console.error('Unexpected error in checkMFAStatus:', error);
        redirectAfterLogin(email);
      }
    },
    [redirectAfterLogin]
  );

  const handleVerifyMFA = async (code: string) => {
    if (!mfaSetupData) return;

    setIsVerifying(true);
    setSetupError('');
    try {
      // Verify the code
      await mfaService.verifySetup(mfaSetupData.deviceId, code);

      // Check status again to confirm
      const statusResponse = await mfaService.getStatus();

      if (statusResponse.data.mfaEnabled) {
        // Update MFA enabled status in localStorage
        localStorage.setItem('mfaEnabled', 'true');

        // Final re-sync before the app loads. This is the last step of a new
        // user's first login, and the only one that mirrors what
        // /complete-mfa-login does on every later login — without it, a session
        // that started before the PPJK link resolved would keep an empty
        // `nomor_ppjk` until the user logged out and back in.
        await hydrateSessionFromMasterUser();

        // Now mark user as authenticated since MFA setup is complete
        setIsAuthenticated(true);

        toast.success(t('lumen.auth.toast.success'), { description: t('lumen.auth.toast.mfaActivated') });

        setShowMFASetup(false);
        // Redirect to app
        redirectAfterLogin(userEmail);
      } else {
        toast.error(t('lumen.auth.toast.error'), { description: t('lumen.auth.toast.mfaNotActive') });
        setSetupError(t('lumen.auth.toast.mfaNotActive'));
      }
    } catch (error) {
      console.error('Error verifying MFA:', error);
      toast.error(t('lumen.auth.toast.error'), { description: t('lumen.auth.toast.setupCodeIncorrect') });
      setSetupError(serverMessage(error) || t('lumen.auth.toast.setupCodeIncorrect'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleMFAVerify = async (code: string) => {
    try {
      setIsVerifying(true);
      setVerifyError('');

      // Call complete-mfa-login endpoint
      const response = await axiosInstance.post('/api/v1/auth/complete-mfa-login', {
        sessionToken,
        code,
      });

      // If successful, extract and store tokens
      if (response.data.success && response.data.data.accessToken) {
        const { accessToken, refreshToken, email, id, ppjkNpwp } = response.data.data;

        // Clear session storage since MFA is complete
        sessionStorage.removeItem('mfaSessionToken');
        sessionStorage.removeItem('mfaEmail');

        completeAuthenticatedSession({
          accessToken,
          refreshToken,
          authData: { email, id, ppjkNpwp },
        });
        await hydrateSessionFromMasterUser();

        toast.success(t('lumen.auth.toast.success'), { description: t('lumen.auth.toast.mfaVerified') });

        // Redirect to app
        setShowMFAVerify(false);
        redirectAfterLogin(email || userEmail);
      }
    } catch (error) {
      console.error('[MFA VERIFY] Error caught:', error);

      toast.error(t('lumen.auth.toast.error'), { description: t('lumen.auth.toast.mfaCodeInvalid') });
      setVerifyError(serverMessage(error) || t('lumen.auth.toast.mfaCodeInvalid'));

      // Clear the PIN input on error but STAY on modal
      setMfaCode('');

      // DO NOT close modal or redirect - user should try again
    } finally {
      setIsVerifying(false);
    }
  };

  const _handleAddDevice = () => {
    // Open the add device modal
    setShowAddDevice(true);
  };

  const handleDeviceAdded = () => {
    // Show success notification when device is added and verified
    toast.success(t('lumen.auth.toast.deviceAdded'), {
      description: t('lumen.auth.toast.deviceAddedMessage'),
      duration: 5000,
    });
  };

  const handleBackToLogin = () => {
    // Clear session storage
    sessionStorage.removeItem('mfaSessionToken');
    sessionStorage.removeItem('mfaEmail');
    // Clear states and go back to login
    setShowMFAVerify(false);
    setSessionToken('');
    setMfaCode('');
    setUserEmail('');
    setVerifyError('');
  };

  // The hook toasts its failures; the same message stays on the form until the next try.
  const passwordError = attempt === 'password' && !isLoading ? loginError : '';
  const cubeServerError = attempt === 'cube' && loginError && loginError !== cubeError ? loginError : '';

  return (
    <>
      {/* MFA Add Device Modal */}
      {showAddDevice && (
        <MFAAddDeviceDialog
          opened={showAddDevice}
          onClose={() => setShowAddDevice(false)}
          onDeviceAdded={handleDeviceAdded}
          sessionToken={sessionToken}
        />
      )}

      {/* MFA Verification Modal (during login) */}
      <MFAVerifyDialog
        open={showMFAVerify}
        code={mfaCode}
        onCodeChange={(value) => {
          setMfaCode(value);
          if (verifyError) setVerifyError('');
          if (value.length === 6) {
            handleMFAVerify(value);
          }
        }}
        isVerifying={isVerifying}
        error={verifyError}
        onBack={handleBackToLogin}
      />

      {/* MFA Setup Modal */}
      {showMFASetup && mfaSetupData && (
        <MFASetupDialog
          opened={showMFASetup}
          qrCodeUrl={mfaSetupData.qrCodeUrl}
          secret={mfaSetupData.secret}
          backupCodes={mfaSetupData.backupCodes}
          deviceId={mfaSetupData.deviceId}
          onVerify={handleVerifyMFA}
          onClose={() => {}}
          isVerifying={isVerifying}
          error={setupError}
        />
      )}

      <AuthFrame
        topRight={<AuthLanguageSwitch />}
        caption={t('auth.showcase.headline')}
        footer={
          <>
            {t('lumen.auth.version')}
            <br />
            {t('lumen.auth.copyright')}
          </>
        }
      >
        <h1 className="font-bold text-fg text-xl leading-snug tracking-[-0.01em]">{t('auth.welcome')}</h1>

        <Form {...form}>
          <form className="mt-8 grid gap-5" noValidate onSubmit={handleSubmit(handleLogin)}>
            {cubeError && (
              <Alert variant="err">
                <CircleAlert aria-hidden="true" />
                <AlertTitle>{t('auth.cubeErrorTitle')}</AlertTitle>
                <AlertDescription>
                  <p>{cubeError}</p>
                  {cubeServerError && <p>{cubeServerError}</p>}
                </AlertDescription>
              </Alert>
            )}
            {passwordError && (
              <Alert variant="err">
                <CircleAlert aria-hidden="true" />
                <AlertTitle>{t('lumen.auth.loginFailed')}</AlertTitle>
                <AlertDescription>
                  <p>{passwordError}</p>
                </AlertDescription>
              </Alert>
            )}

            <FormField
              name="email"
              control={control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.email')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="username"
                      placeholder={t('auth.emailPlaceholder')}
                      className="h-10"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="password"
              control={control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.password')}</FormLabel>
                  <FormControl>
                    <PasswordInput
                      autoComplete="current-password"
                      placeholder={t('auth.passwordPlaceholder')}
                      className="h-10"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              size="lg"
              className="mt-2 w-full"
              disabled={isLoading}
              aria-busy={isLoading || undefined}
            >
              {isLoading && <Spinner />}
              {t('auth.signIn')}
            </Button>

            <div className="flex items-center gap-3 text-fg-muted text-xs">
              <span className="h-px flex-1 bg-line" />
              {t('auth.or')}
              <span className="h-px flex-1 bg-line" />
            </div>

            <Button
              variant="outline"
              type="button"
              size="lg"
              className="w-full"
              disabled={isLoading || isCubeLoading}
              aria-busy={isCubeLoading || undefined}
              onClick={handleCubeLogin}
            >
              {isCubeLoading ? (
                <Spinner />
              ) : (
                <img
                  src="/image/png/ccn.png"
                  alt={t('lumen.auth.cubeLogoAlt')}
                  className="size-6 rounded-tag object-contain"
                />
              )}
              <span>{t('auth.cubeLogin')}</span>
            </Button>
          </form>
        </Form>

        <div className="mt-6 text-center">
          <Button
            variant="link"
            type="button"
            className="text-xs"
            onClick={() => navigate(pageUrl.approval.reviewerLogin)}
          >
            {t('auth.reviewerLogin')}
          </Button>
        </div>
      </AuthFrame>
    </>
  );
}
